import {useLayoutEffect, useState} from 'react';
import {Move} from 'lucide-react';

import {suppressClickAfterDrag} from '../lib/pointer-drag';
import {
  chooseGripSide,
  GRIP_CLEARANCE,
  GRIP_POSITION,
  TABLE_CORNERS,
  type GripSide,
  type TableCorner,
} from '../lib/table-grip';
import type {PhotoRequirement} from '../lib/types';

/**
 * Everything drawn AROUND a table rather than inside it: the two outer sizing
 * lines, the four scaling corners, and the grip that moves the whole element.
 *
 * Separate from `TableElement` because it shares nothing with the cell-render
 * loop — it needs the wrapper's box, the three drag starters, and nothing else.
 * Kept together because the three are one design: an edge is a line you nudge, a
 * corner is a handle you grab, and the grip is deliberately clear of both so
 * "move" can never be a near-miss for "resize".
 */
export function TableChrome({
  wrapperRef,
  element,
  readOnly,
  onGripPress,
  startColResize,
  startRowResize,
  startOuterResize,
}: {
  wrapperRef: React.RefObject<HTMLDivElement | null>;
  /** The table this chrome belongs to. Its geometry is what tells the grip to
   *  re-measure after a move — see `useGripSide`. */
  element: PhotoRequirement;
  readOnly: boolean;
  /** The grip's press, before the drag: select the table and drop whatever was
   *  selected inside it. */
  onGripPress: () => void;
  startColResize: (colIdx: number, e: React.MouseEvent, side?: 'near' | 'far') => void;
  startRowResize: (rowIdx: number, e: React.MouseEvent, side?: 'near' | 'far') => void;
  startOuterResize: (corner: TableCorner, e: React.MouseEvent) => void;
}) {
  const gripSide = useGripSide(wrapperRef, element);

  return (
    <>
      {/* ── The table's outer edges ────────────────────────────────────────
          Each sizes the ONE row or column it borders, exactly like an internal
          line — because that is what it is: the left line is the first column's
          left edge, the top line is the first row's top edge. (The right and
          bottom outer lines are already the last column's and last row's own
          handles, drawn per cell, so they are not repeated here.) */}
      <div
        data-table-edge="left"
        className="absolute inset-y-0 left-0 hover:bg-indigo-400/40 transition-colors"
        style={{width: 6, transform: 'translateX(-50%)', cursor: 'col-resize', zIndex: 20}}
        onMouseDown={(e) => startColResize(0, e, 'near')}
      />
      <div
        data-table-edge="top"
        className="absolute inset-x-0 top-0 hover:bg-indigo-400/40 transition-colors"
        style={{height: 6, transform: 'translateY(-50%)', cursor: 'row-resize', zIndex: 20}}
        onMouseDown={(e) => startRowResize(0, e, 'near')}
      />

      {/* ── Move the whole table ───────────────────────────────────────────
          A table's body belongs to its cells: pressing one selects or edits it,
          so there is nowhere on a table to grab the ELEMENT. This grip is that
          place. Revealed on hover rather than always drawn, so a page of tables
          is not a page of floating buttons. */}
      {!readOnly && (
        <div
          data-table-move
          data-grip-side={gripSide}
          aria-label="Move table"
          // **The grip owns its click.** A press on a control is not a click on
          // whatever surrounds it, and the page sheet's own `onClick` clears the
          // selection — so a click allowed past here can undo, one event later,
          // the selection the press just made: the toolbar appears and then
          // vanishes. The MOUSEDOWN still bubbles, because that is the drag, and
          // stopping it would trade a flickering toolbar for a table that cannot
          // be moved.
          onClick={(e) => e.stopPropagation()}
          title="Drag to move the whole table"
          onMouseDown={() => {
            onGripPress();
            suppressClickAfterDrag();
          }}
          className={`absolute flex h-7 w-7 cursor-move items-center justify-center rounded-full border border-indigo-300 bg-white text-indigo-500 opacity-0 shadow-sm transition-opacity hover:bg-indigo-50 group-hover/table:opacity-100 ${GRIP_POSITION[gripSide]}`}
          style={{zIndex: 22}}
        >
          <Move className="h-4 w-4" strokeWidth={2} />
        </div>
      )}

      {/* ── The four corners ───────────────────────────────────────────────
          The only gesture that resizes the WHOLE table, scaling every column and
          row by the same factor so the structure is preserved. Filled squares
          rather than strips, and above the edges (`zIndex`), so the two modes
          are told apart by eye and by the cursor before the mouse goes down. */}
      {TABLE_CORNERS.map(({dir, className, cursor}) => (
        <div
          key={dir}
          data-table-corner={dir}
          aria-label={`Resize table (${dir})`}
          className={`absolute bg-indigo-400 hover:bg-indigo-600 transition-colors rounded-sm ring-1 ring-white ${className}`}
          style={{width: 10, height: 10, cursor, zIndex: 21}}
          onMouseDown={(e) => startOuterResize(dir, e)}
        />
      ))}
    </>
  );
}

/**
 * Which side of the table the move grip sits on.
 *
 * The rule itself is `chooseGripSide`; this only measures the two rectangles it
 * needs. Re-measured whenever the table MOVES as well as when it resizes, and
 * the move is the case that matters: a `ResizeObserver` never fires for an
 * element that changed position and not size, so a table dragged against the
 * right margin would keep a grip it had already pushed off the page — and the
 * grip is what a further drag needs, so the author would be stuck.
 *
 * A page that has not laid out yet measures zero and keeps the default; there is
 * nothing to be wrong about yet, and the effect runs again when there is.
 */
function useGripSide(
  wrapperRef: React.RefObject<HTMLDivElement | null>,
  element: PhotoRequirement,
): GripSide {
  const [gripSide, setGripSide] = useState<GripSide>('right');
  // Destructured because the EFFECT depends on these four numbers, and a table
  // that only moved is the case a `ResizeObserver` cannot report.
  const {x, y, width, height, position_mode} = element;

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    const sheet = wrapper?.closest('[data-page-sheet]');
    if (!wrapper || !sheet) return;
    const measure = () => {
      const page = sheet.getBoundingClientRect();
      if (page.width === 0) return;
      setGripSide(chooseGripSide(wrapper.getBoundingClientRect(), page, GRIP_CLEARANCE));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(wrapper);
    observer.observe(sheet);
    return () => observer.disconnect();
  }, [wrapperRef, x, y, width, height, position_mode]);

  return gripSide;
}
